import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  IconButton,
  useMediaQuery,
  CircularProgress,
  Alert,
  Container,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import EditEngineerModal from "../../components/Inhouse/EditEngineerModal";
import CustomTooltip from "../../components/CustomTooltip";
import { useNavigate } from "react-router-dom";
import AppToast from "../../components/AppToast";

const PRIMARY = COLORS.PRIMARY || "#6A1B9A";

// Utility functions for date handling
const getCurrentWeekDates = () => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate Monday of current week
  let monday = new Date(today);
  if (currentDay === 0) {
    // If Sunday, get previous Monday
    monday.setDate(today.getDate() - 6);
  } else {
    monday.setDate(today.getDate() - (currentDay - 1));
  }

  // Generate Monday to Saturday dates
  const weekDates = [];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  for (let i = 0; i < 6; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);

    weekDates.push({
      date: date,
      day: dayNames[i],
      formattedDate: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
      // Add ISO string for consistent comparison
      isoString: date.toISOString().split("T")[0],
    });
  }

  return weekDates;
};

// Month-based functions for MPR
const getCurrentMonthDates = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Generate dates for current month (1st to last day)
  const monthDates = [];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(currentYear, currentMonth, i);
    monthDates.push({
      date: date,
      day: date.toLocaleDateString("en-US", { weekday: "short" }),
      formattedDate: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
    });
  }

  return monthDates;
};

const getMonthDates = (type) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  let targetMonth, targetYear;

  switch (type) {
    case "last":
      targetMonth = currentMonth - 1;
      targetYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      break;
    case "current":
      targetMonth = currentMonth;
      targetYear = currentYear;
      break;
    case "next":
      targetMonth = currentMonth + 1;
      targetYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      break;
    default:
      targetMonth = currentMonth;
      targetYear = currentYear;
  }

  // Generate dates for the target month
  const monthDates = [];
  const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(targetYear, targetMonth, i);
    monthDates.push({
      date: date,
      day: date.toLocaleDateString("en-US", { weekday: "short" }),
      formattedDate: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
    });
  }

  return monthDates;
};

const getMonthRangeString = (monthDates) => {
  if (!monthDates || monthDates.length === 0) return "";

  const firstDate = monthDates[0].date;
  const lastDate = monthDates[monthDates.length - 1].date;

  const startStr = firstDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });

  const endStr = lastDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });

  return `${startStr} to ${endStr}`;
};

const isPastMonth = (monthDates) => {
  if (!monthDates || monthDates.length === 0) return false;
  const lastDay = monthDates[monthDates.length - 1].date;
  return isPastDate(lastDay);
};

const isCurrentMonth = (monthDates) => {
  if (!monthDates || monthDates.length === 0) return false;
  const currentMonth = getCurrentMonthDates();
  const firstDay = monthDates[0].date;
  const currentFirstDay = currentMonth[0].date;

  return firstDay.toDateString() === currentFirstDay.toDateString();
};

const isFutureMonth = (monthDates) => {
  if (!monthDates || monthDates.length === 0) return false;
  const firstDay = monthDates[0].date;
  return isFutureDate(firstDay);
};

// Get week dates for a specific week type
const getWeekDates = (weekType = "current") => {
  const today = new Date();
  const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  let monday = new Date(today);

  // Calculate Monday of current week
  if (currentDay === 0) {
    monday.setDate(today.getDate() - 6);
  } else {
    monday.setDate(today.getDate() - (currentDay - 1));
  }

  // Adjust for week type
  if (weekType === "last") {
    monday.setDate(monday.getDate() - 7);
  } else if (weekType === "next") {
    monday.setDate(monday.getDate() + 7);
  }

  // Generate Monday to Saturday dates
  const weekDates = [];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  for (let i = 0; i < 6; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);

    weekDates.push({
      date: date,
      day: dayNames[i],
      formattedDate: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
    });
  }

  return weekDates;
};

// Get week range string
const getWeekRangeString = (weekDates) => {
  if (!weekDates || weekDates.length === 0) return "";
  const startDate = weekDates[0].date;
  const endDate = weekDates[5].date;

  const startStr = startDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });

  const endStr = endDate.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });

  return `${startStr} to ${endStr}`;
};

const formatDateForAPI = (date) => {
  if (typeof date === "string") {
    date = new Date(date);
  }
  return date.toISOString().split("T")[0];
};

const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate.getTime() === today.getTime();
};

const isPastDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

const isFutureDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate > today;
};

const isPastWeek = (weekDates) => {
  if (!weekDates || weekDates.length === 0) return false;
  const lastDay = weekDates[5].date;
  return isPastDate(lastDay);
};

const isCurrentWeek = (weekDates) => {
  if (!weekDates || weekDates.length === 0) return false;
  const currentWeek = getCurrentWeekDates();
  const firstDay = weekDates[0].date;
  const currentFirstDay = currentWeek[0].date;

  return firstDay.toDateString() === currentFirstDay.toDateString();
};

const isFutureWeek = (weekDates) => {
  if (!weekDates || weekDates.length === 0) return false;
  const firstDay = weekDates[0].date;
  return isFutureDate(firstDay);
};

const statusColor = (status) => {
  switch (status) {
    case "success":
      return "#8ED081"; // Green - Report exists (past/today)
    case "warning":
      return "#FAD97A"; // Orange/Yellow - Today/Current week/month, no report
    case "error":
      return "#FF7C7C"; // Red - No report (past or future)
    case "info":
      return "#FFFFFF"; // White - Future date with report
    default:
      return "#FFFFFF";
  }
};

// Helper function to check if user is admin
const isUserAdmin = (userRole) => {
  return (
    userRole === "ADMIN" ||
    userRole === "SUPERADMIN" ||
    userRole === "admin" ||
    userRole === "superadmin"
  );
};

// Status styles matching the InHouseProjects component
const statusStyles = {
  ONTRACK: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  REPORTMISSING: {
    bgcolor: "#FF7C7C",
    color: "#ffffff",
  },
  "On Track": {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  Completed: {
    bgcolor: "#4CAF50",
    color: "#ffffff",
  },
  "On Hold": {
    bgcolor: "#FF9800",
    color: "#ffffff",
  },
  Cancelled: {
    bgcolor: "#F44336",
    color: "#ffffff",
  },
  ACTIVE: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  COMPLETED: {
    bgcolor: "#4CAF50",
    color: "#ffffff",
  },
  ON_HOLD: {
    bgcolor: "#FF9800",
    color: "#ffffff",
  },
  CANCELLED: {
    bgcolor: "#F44336",
    color: "#ffffff",
  },
};

const ProjectOverview = () => {
  const { projectId } = useParams();
  const { token, userData } = useAuth();
  const isMobile = useMediaQuery("(max-width:900px)");
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isExtraSmall = useMediaQuery("(max-width:400px)");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [dprData, setDprData] = useState([]);
  const [wprData, setWprData] = useState([]);
  const [wmrData, setWmrData] = useState([]);
  const [mprData, setMprData] = useState([]);
  const [momData, setMomData] = useState([]);
  const [editEngineerModalOpen, setEditEngineerModalOpen] = useState(false);
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Get current week dates for DPR cards
  const currentWeekDates = getCurrentWeekDates();

  // Get week dates for WPR/WMR cards
  const lastWeekDates = getWeekDates("last");
  const currentWPRWeekDates = getWeekDates("current");
  const nextWeekDates = getWeekDates("next");

  const weekCards = [
    {
      label: "Last Week",
      range: getWeekRangeString(lastWeekDates),
      weekType: "last",
      weekDates: lastWeekDates,
    },
    {
      label: "Current Week",
      range: getWeekRangeString(currentWPRWeekDates),
      weekType: "current",
      weekDates: currentWPRWeekDates,
    },
    {
      label: "Next Week",
      range: getWeekRangeString(nextWeekDates),
      weekType: "next",
      weekDates: nextWeekDates,
    },
  ];

  // Get month dates for MPR cards
  const lastMonthDates = getMonthDates("last");
  const currentMonthDates = getMonthDates("current");
  const nextMonthDates = getMonthDates("next");

  const monthCards = [
    {
      label: "Last Month",
      range: getMonthRangeString(lastMonthDates),
      monthType: "last",
      monthDates: lastMonthDates,
    },
    {
      label: "Current Month",
      range: getMonthRangeString(currentMonthDates),
      monthType: "current",
      monthDates: currentMonthDates,
    },
    {
      label: "Next Month",
      range: getMonthRangeString(nextMonthDates),
      monthType: "next",
      monthDates: nextMonthDates,
    },
  ];

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  // Close toast function
  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // Snackbar shim for EditEngineerModal
  const showSnackbar = (message, severity) => {
    showToast(
      severity,
      severity.charAt(0).toUpperCase() + severity.slice(1),
      message,
    );
  };

  // Refresh DPR data function
  const refreshDPRData = async () => {
    try {
      setRefreshing(true);
      await fetchDPRData(projectId);
      showToast("success", "Refreshed", "DPR data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing DPR data:", error);
      showToast("error", "Error", "Failed to refresh DPR data");
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch project data with dynamic status
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config,
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);

          // Fetch all report data in parallel
          await Promise.all([
            fetchDPRData(projectResponse.data.project._id),
            fetchWPRData(projectResponse.data.project._id),
            fetchWMRData(projectResponse.data.project._id),
            fetchMPRData(projectResponse.data.project._id),
            fetchMOMData(projectResponse.data.project._id),
          ]);
        } else {
          setError("Project not found");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Failed to load project data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchProject();
    }
  }, [projectId, token]);

  // Download DPR Report
  const handleDownloadDPRReport = async (date) => {
    try {
      setDownloading({ type: "dpr", date });
      const dateString = formatDateForAPI(new Date(date));

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      const response = await axios.get(
        `${url}/api/dpr/download-report/${projectId}`,
        {
          ...config,
          params: {
            dateFrom: dateString,
            dateTo: dateString,
          },
        },
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const dateObj = new Date(date);
      const dateStr = dateObj.toLocaleDateString("en-GB").replace(/\//g, "-");
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "DPR";
      link.download = `DPR_${projectName}_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "DPR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading DPR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download DPR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(null);
    }
  };

  // Download WPR Report
  const handleDownloadWPRReport = async (card) => {
    try {
      setDownloading({ type: "wpr", id: card.label });

      // Find the WPR ID for this week
      const weekWPR = wprData?.find((wpr) => {
        if (
          !wpr.weekStartDate ||
          !wpr.weekEndDate ||
          !card.weekDates ||
          card.weekDates.length === 0
        ) {
          return false;
        }

        const weekStart = card.weekDates[0].date;
        const weekEnd = card.weekDates[5].date;

        // Normalize dates (remove time part)
        const normalizeDate = (date) => {
          const d = new Date(date);
          d.setHours(0, 0, 0, 0);
          return d;
        };

        const normalizedWeekStart = normalizeDate(weekStart);
        const normalizedWeekEnd = normalizeDate(weekEnd);
        const wprStart = normalizeDate(new Date(wpr.weekStartDate));
        const wprEnd = normalizeDate(new Date(wpr.weekEndDate));

        return (
          wprStart.getTime() === normalizedWeekStart.getTime() &&
          wprEnd.getTime() === normalizedWeekEnd.getTime()
        );
      });

      if (!weekWPR) {
        showToast("error", "Error", "WPR report not found for this week.");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      const response = await axios.get(
        `${url}/api/wpr/download/${weekWPR._id}/report`,
        config,
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "WPR";
      const weekStr = `${formatDateForAPI(card.weekDates[0].date)}_to_${formatDateForAPI(card.weekDates[5].date)}`;
      link.download = `WPR_${projectName}_${weekStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "WPR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading WPR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download WPR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(null);
    }
  };

  // Download WMR Report
  const handleDownloadWMRReport = async (card) => {
    try {
      setDownloading({ type: "wmr", id: card.label });

      // Find the WMR ID for this week
      const weekWMR = wmrData?.find((wmr) => {
        if (
          !wmr.weekStartDate ||
          !wmr.weekEndDate ||
          !card.weekDates ||
          card.weekDates.length === 0
        ) {
          return false;
        }

        const weekStart = card.weekDates[0].date;
        const weekEnd = card.weekDates[5].date;

        // Normalize dates (remove time part)
        const normalizeDate = (date) => {
          const d = new Date(date);
          d.setHours(0, 0, 0, 0);
          return d;
        };

        const normalizedWeekStart = normalizeDate(weekStart);
        const normalizedWeekEnd = normalizeDate(weekEnd);
        const wmrStart = normalizeDate(new Date(wmr.weekStartDate));
        const wmrEnd = normalizeDate(new Date(wmr.weekEndDate));

        return (
          wmrStart.getTime() === normalizedWeekStart.getTime() &&
          wmrEnd.getTime() === normalizedWeekEnd.getTime()
        );
      });

      if (!weekWMR) {
        showToast("error", "Error", "WMR report not found for this week.");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      const response = await axios.get(
        `${url}/api/wmr/download/${weekWMR._id}/report`,
        config,
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "WMR";
      const weekStr = `${formatDateForAPI(card.weekDates[0].date)}_to_${formatDateForAPI(card.weekDates[5].date)}`;
      link.download = `WMR_${projectName}_${weekStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "WMR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading WMR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download WMR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(null);
    }
  };

  // Download MPR Report
  const handleDownloadMPRReport = async (card) => {
    try {
      setDownloading({ type: "mpr", id: card.label });

      // Find the MPR ID for this month
      const monthMPR = mprData?.find((mpr) => {
        if (
          !mpr.monthStartDate ||
          !card.monthDates ||
          card.monthDates.length === 0
        ) {
          return false;
        }

        const monthStart = card.monthDates[0].date;

        // Normalize dates (remove time part)
        const normalizeDate = (date) => {
          const d = new Date(date);
          d.setHours(0, 0, 0, 0);
          return d;
        };

        const normalizedMonthStart = normalizeDate(monthStart);
        const mprStart = normalizeDate(new Date(mpr.monthStartDate));

        return mprStart.getTime() === normalizedMonthStart.getTime();
      });

      if (!monthMPR) {
        showToast("error", "Error", "MPR report not found for this month.");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        responseType: "blob",
      };

      const response = await axios.get(
        `${url}/api/mpr/pdf/${monthMPR._id}`,
        config,
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const urlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlBlob;

      // Generate filename
      const projectName =
        project?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "MPR";
      const monthStr = card.monthDates[0].date
        .toLocaleDateString("en-GB", {
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "_");
      link.download = `MPR_${projectName}_${monthStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlBlob);

      showToast("success", "Success", "MPR report downloaded successfully!");
    } catch (err) {
      console.error("Error downloading MPR report:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to download MPR report. Please try again.";
      showToast("error", "Error", errorMessage);
    } finally {
      setDownloading(null);
    }
  };

  // Fetch merged DPR data for a specific date
  const fetchDPRData = async (projectId) => {
    try {
      // Get current date for the week view
      const today = new Date();

      // Calculate start and end of current week (Monday to Saturday)
      const currentDay = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));

      const saturday = new Date(monday);
      saturday.setDate(monday.getDate() + 5);

      // Format dates for API
      const formatDateForAPI = (date) => {
        return date.toISOString().split("T")[0];
      };

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId: projectId,
          dateFrom: formatDateForAPI(monday),
          dateTo: formatDateForAPI(saturday),
          page: 1,
          limit: 10, // Get all DPRs for the week
        },
      };

      console.log("Fetching merged DPR data for week:", {
        from: formatDateForAPI(monday),
        to: formatDateForAPI(saturday),
      });

      // Use the merged DPR API
      const response = await axios.get(`${url}/api/dpr/merged`, config);

      if (response.data.mergedDprs && Array.isArray(response.data.mergedDprs)) {
        console.log(
          `Found ${response.data.mergedDprs.length} merged DPR records`,
        );

        // Log all DPR dates for debugging
        response.data.mergedDprs.forEach((mergedDpr, index) => {
          if (mergedDpr.reportDate) {
            const date = new Date(mergedDpr.reportDate);
            console.log(
              `Merged DPR ${index + 1}: ${date.toISOString().split("T")[0]} - Sections: ${mergedDpr.sectionTypes?.length || 0}`,
            );
          }
        });

        setDprData(response.data.mergedDprs);
      } else {
        console.log("No merged DPR data found");
        setDprData([]);
      }
    } catch (err) {
      console.error("Error fetching merged DPR data:", err);

      // Fallback to the old API if merged endpoint fails
      try {
        console.log("Trying fallback to regular DPR API...");

        const fallbackConfig = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          params: {
            projectId: projectId,
            page: 1,
            limit: 200,
          },
        };

        const fallbackResponse = await axios.get(
          `${url}/api/dpr/fetch`,
          fallbackConfig,
        );

        if (
          fallbackResponse.data.dprs &&
          Array.isArray(fallbackResponse.data.dprs)
        ) {
          console.log(
            `Fallback found ${fallbackResponse.data.dprs.length} DPR records`,
          );
          setDprData(fallbackResponse.data.dprs);
        } else {
          setDprData([]);
        }
      } catch (fallbackErr) {
        console.error("Fallback also failed:", fallbackErr);
        setDprData([]);
      }
    }
  };

  // Fetch DPR for a specific date using merged API
  const fetchDPRForDate = async (projectId, date) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId: projectId,
          reportDate: formatDateForAPI(date),
        },
      };

      console.log(
        `Fetching merged DPR for specific date: ${formatDateForAPI(date)}`,
      );

      const response = await axios.get(
        `${url}/api/dpr/merged/by-project-date`,
        config,
      );

      if (response.data.mergedDPR) {
        console.log(
          `Found merged DPR for ${formatDateForAPI(date)} with ${response.data.totalSections} sections`,
        );
        return response.data.mergedDPR;
      }

      return null;
    } catch (err) {
      console.error(`Error fetching DPR for date ${date}:`, err);
      return null;
    }
  };

  // Fetch WPR data
  const fetchWPRData = async (projectId) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId: projectId,
        },
      };

      const response = await axios.get(
        `${url}/api/wpr/fetch-by-project/${projectId}`,
        config,
      );

      if (response.data.wprs && Array.isArray(response.data.wprs)) {
        setWprData(response.data.wprs);
      } else {
        setWprData([]);
      }
    } catch (err) {
      console.error("Error fetching WPR data:", err);
      setWprData([]);
    }
  };

  // Fetch WMR data
  const fetchWMRData = async (projectId) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId: projectId,
        },
      };

      const response = await axios.get(
        `${url}/api/wmr/fetch-by-project/${projectId}`,
        config,
      );

      if (response.data.wmrs && Array.isArray(response.data.wmrs)) {
        setWmrData(response.data.wmrs);
      } else {
        setWmrData([]);
      }
    } catch (err) {
      console.error("Error fetching WMR data:", err);
      setWmrData([]);
    }
  };

  // Fetch MPR data
  const fetchMPRData = async (projectId) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId: projectId,
        },
      };

      const response = await axios.get(
        `${url}/api/mpr/fetch-by-project/${projectId}`,
        config,
      );

      if (response.data.mprs && Array.isArray(response.data.mprs)) {
        setMprData(response.data.mprs);
      } else {
        setMprData([]);
      }
    } catch (err) {
      console.error("Error fetching MPR data:", err);
      setMprData([]);
    }
  };

  // Fetch MOM data
  const fetchMOMData = async (projectId) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const response = await axios.get(
        `${url}/api/mom/project/${projectId}`,
        config,
      );

      if (response.data.moms && Array.isArray(response.data.moms)) {
        setMomData(response.data.moms);
      } else {
        setMomData([]);
      }
    } catch (err) {
      console.error("Error fetching MOM data:", err);
      setMomData([]);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case "ONTRACK":
        return "On Track";
      case "REPORTMISSING":
        return "Report Missing";
      case "ACTIVE":
        return "On Track";
      case "COMPLETED":
        return "Completed";
      case "ON_HOLD":
        return "On Hold";
      case "CANCELLED":
        return "Cancelled";
      default:
        return status || "On Track";
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusText = getStatusText(status);
    return (
      statusStyles[statusText] ||
      statusStyles[status] ||
      statusStyles["On Track"]
    );
  };

  // Check if DPR exists for a specific date - UPDATED FOR MERGED DPR
  const checkDPRExistsForDate = (date) => {
    if (!date || !dprData || dprData.length === 0) {
      console.log(`No DPR data or invalid date: ${date}`);
      return false;
    }

    // Convert input date to YYYY-MM-DD format
    const normalizeDate = (dateToNormalize) => {
      if (!dateToNormalize) return null;

      const d = new Date(dateToNormalize);
      d.setHours(0, 0, 0, 0);

      // Get year, month, day
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const targetDateStr = normalizeDate(date);
    console.log(`Checking DPR for date: ${targetDateStr}`);

    // Check all merged DPRs
    for (const mergedDpr of dprData) {
      if (mergedDpr.reportDate) {
        const dprDateStr = normalizeDate(mergedDpr.reportDate);

        console.log(
          `Comparing with merged DPR date: ${dprDateStr}, Sections: ${mergedDpr.sectionTypes?.length || 0}`,
        );

        if (dprDateStr === targetDateStr) {
          console.log(
            `✓ DPR FOUND for ${targetDateStr} with ${mergedDpr.sectionCount || 0} sections`,
          );
          return true;
        }
      }
    }

    // Fallback: Check if we have regular DPR data (not merged)
    // This handles the case where fallback to regular API was used
    if (dprData[0] && dprData[0].reportType) {
      console.log("Checking regular DPR data (non-merged)...");
      for (const group of dprData) {
        if (group.reportDate) {
          const dprDateStr = normalizeDate(group.reportDate);

          console.log(
            `Comparing with regular DPR date: ${dprDateStr}, Type: ${group.reportType}`,
          );

          if (dprDateStr === targetDateStr) {
            console.log(
              `✓ DPR FOUND in regular data for ${targetDateStr}: ${group.reportType}`,
            );
            return true;
          }
        }
      }
    }

    console.log(`✗ No DPR found for ${targetDateStr}`);
    return false;
  };

  // Check if WPR exists for given week dates
  const checkWPRExistsForWeek = (weekDates) => {
    if (
      !weekDates ||
      weekDates.length === 0 ||
      !wprData ||
      wprData.length === 0
    ) {
      return false;
    }

    const weekStart = weekDates[0].date;
    const weekEnd = weekDates[5].date;

    // Normalize dates (remove time part)
    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const normalizedWeekStart = normalizeDate(weekStart);
    const normalizedWeekEnd = normalizeDate(weekEnd);

    return wprData.some((wpr) => {
      const wprStart = wpr.weekStartDate
        ? normalizeDate(new Date(wpr.weekStartDate))
        : null;
      const wprEnd = wpr.weekEndDate
        ? normalizeDate(new Date(wpr.weekEndDate))
        : null;

      if (!wprStart || !wprEnd) return false;

      // Check if the WPR covers the same week
      return wprStart === normalizedWeekStart && wprEnd === normalizedWeekEnd;
    });
  };

  // Check if WMR exists for given week dates
  const checkWMRExistsForWeek = (weekDates) => {
    if (
      !weekDates ||
      weekDates.length === 0 ||
      !wmrData ||
      wmrData.length === 0
    ) {
      return null;
    }

    const weekStart = weekDates[0].date;
    const weekEnd = weekDates[5].date;

    // Normalize dates (remove time part)
    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const normalizedWeekStart = normalizeDate(weekStart);
    const normalizedWeekEnd = normalizeDate(weekEnd);

    const foundWMR = wmrData.find((wmr) => {
      const wmrStart = wmr.weekStartDate
        ? normalizeDate(new Date(wmr.weekStartDate))
        : null;
      const wmrEnd = wmr.weekEndDate
        ? normalizeDate(new Date(wmr.weekEndDate))
        : null;

      if (!wmrStart || !wmrEnd) return false;

      // Check if the WMR covers the same week
      return wmrStart === normalizedWeekStart && wmrEnd === normalizedWeekEnd;
    });

    return foundWMR || null;
  };

  // Check if MPR exists for given month dates
  const checkMPRExistsForMonth = (monthDates) => {
    if (
      !monthDates ||
      monthDates.length === 0 ||
      !mprData ||
      mprData.length === 0
    ) {
      return null;
    }

    const normalizeDate = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const monthStart = monthDates[0].date;
    const normalizedMonthStart = normalizeDate(monthStart);

    const foundMPR = mprData.find((mpr) => {
      const mprStart = mpr.monthStartDate
        ? normalizeDate(new Date(mpr.monthStartDate))
        : null;

      if (!mprStart) return false;
      // Check if MPR covers the same month and project
      const mprProjectId = mpr.projectId?._id || mpr.projectId;
      return mprStart === normalizedMonthStart && mprProjectId === projectId;
    });

    return foundMPR || null;
  };

  // Get DPR status and icons for each day - UPDATED WITH BETTER DEBUGGING
  const getDPRCards = () => {
    const isAdmin = isUserAdmin(userData?.role);

    return currentWeekDates.map(({ day, date, formattedDate, isoString }) => {
      const actualDate = date;
      const pastDate = isPastDate(actualDate);
      const futureDate = isFutureDate(actualDate);
      const today = isToday(actualDate);
      const hasDPR = checkDPRExistsForDate(actualDate);

      console.log(`Card ${formattedDate} (${isoString}):`, {
        hasDPR,
        past: pastDate,
        today,
        future: futureDate,
        date: actualDate.toISOString(),
      });

      // Determine status - SIMPLIFIED LOGIC
      let status;
      if (hasDPR) {
        // DPR exists
        if (futureDate) {
          status = "info"; // White - Future date with DPR
        } else {
          status = "success"; // Green - Past/Today with DPR
        }
      } else {
        // No DPR exists
        if (today) {
          status = "warning"; // Orange - Today without DPR
        } else {
          status = "error"; // Red - Past/Future without DPR
        }
      }

      // Get icons based on status
      const icons = [];

      // ADMIN LOGIC
      if (isAdmin) {
        if (hasDPR) {
          // DPR exists - admin sees all icons
          icons.push("eye"); // View
          icons.push("download1"); // Download
          icons.push("edit"); // Edit
        } else {
          // No DPR exists
          if (today || futureDate) {
            // Today or future - show edit to create
            icons.push("edit");
          } else {
            // Past date - show close4
            icons.push("close4");
          }
        }
      }
      // ENGINEER LOGIC
      else {
        if (hasDPR) {
          // DPR exists
          icons.push("eye"); // Always show view

          if (today) {
            // Today - show edit
            icons.push("edit");
          } else if (pastDate) {
            // Past date - show download
            icons.push("download1");
          }
          // Future date with DPR - no icons (shouldn't happen)
        } else {
          // No DPR exists
          if (today) {
            // Today - show edit to create
            icons.push("edit");
          } else {
            // Past or future date - show close4
            icons.push("close4");
          }
        }
      }

      return {
        day,
        date: formattedDate,
        status,
        icons,
        hasDPR,
        actualDate: date,
        isFutureDate: futureDate,
        isToday: today,
        isPastDate: pastDate,
        isoString: isoString, // Add for debugging
      };
    });
  };

  // Get WPR status and icons for each week
  const getWPRCards = () => {
    const isAdmin = isUserAdmin(userData?.role);

    return weekCards.map((card) => {
      const hasWPR = checkWPRExistsForWeek(card.weekDates);
      const isPast = isPastWeek(card.weekDates);
      const isCurrent = isCurrentWeek(card.weekDates);
      const isFuture = isFutureWeek(card.weekDates);

      // Determine status
      let status;
      if (hasWPR) {
        if (isFuture) {
          status = "info"; // White - Future week with WPR
        } else {
          status = "success"; // Green - Past/Current week with WPR
        }
      } else {
        if (isCurrent) {
          status = "warning"; // Orange - Current week without WPR
        } else {
          status = "error"; // Red - Past/Future week without WPR
        }
      }

      // Get icons based on status
      const icons = [];

      // ADMIN LOGIC
      if (isAdmin) {
        if (hasWPR) {
          icons.push("eye"); // View
          icons.push("download1"); // Download
          icons.push("edit"); // Edit
        } else {
          if (isCurrent || isFuture) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }
      // ENGINEER LOGIC
      else {
        if (hasWPR) {
          icons.push("eye"); // View

          if (isCurrent) {
            icons.push("edit"); // Edit
          } else if (isPast) {
            icons.push("download1"); // Download
          }
        } else {
          if (isCurrent) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }

      return {
        ...card,
        status,
        icons,
        hasWPR,
        isPast,
        isCurrent,
        isFuture,
      };
    });
  };

  // Get WMR status and icons for each week
  const getWMRCards = () => {
    const isAdmin = isUserAdmin(userData?.role);

    return weekCards.map((card) => {
      const wmr = checkWMRExistsForWeek(card.weekDates);
      const hasWMR = !!wmr;
      const isPast = isPastWeek(card.weekDates);
      const isCurrent = isCurrentWeek(card.weekDates);
      const isFuture = isFutureWeek(card.weekDates);

      // Determine status
      let status;
      if (hasWMR) {
        if (isFuture) {
          status = "info"; // White - Future week with WMR
        } else {
          status = "success"; // Green - Past/Current week with WMR
        }
      } else {
        if (isCurrent) {
          status = "warning"; // Orange - Current week without WMR
        } else {
          status = "error"; // Red - Past/Future week without WMR
        }
      }

      // Get icons based on status
      const icons = [];

      // ADMIN LOGIC
      if (isAdmin) {
        if (hasWMR) {
          icons.push("eye"); // View
          icons.push("download1"); // Download
          icons.push("edit"); // Edit
        } else {
          if (isCurrent || isFuture) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }
      // ENGINEER LOGIC
      else {
        if (hasWMR) {
          icons.push("eye"); // View

          if (isCurrent) {
            icons.push("edit"); // Edit
          } else if (isPast) {
            icons.push("download1"); // Download
          }
        } else {
          if (isCurrent) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }

      return {
        ...card,
        status,
        icons,
        hasWMR,
        wmr,
        isPast,
        isCurrent,
        isFuture,
      };
    });
  };

  // Get MPR status and icons for each month
  const getMPRCards = () => {
    const isAdmin = isUserAdmin(userData?.role);

    return monthCards.map((card) => {
      const mpr = checkMPRExistsForMonth(card.monthDates);
      const hasMPR = !!mpr;
      const isPast = isPastMonth(card.monthDates);
      const isCurrent = isCurrentMonth(card.monthDates);
      const isFuture = isFutureMonth(card.monthDates);

      // Determine status
      let status;
      if (hasMPR) {
        if (isFuture) {
          status = "info"; // White - Future month with MPR
        } else {
          status = "success"; // Green - Past/Current month with MPR
        }
      } else {
        if (isCurrent) {
          status = "warning"; // Orange - Current month without MPR
        } else {
          status = "error"; // Red - Past/Future month without MPR
        }
      }

      // Get icons based on status
      const icons = [];

      // ADMIN LOGIC
      if (isAdmin) {
        if (hasMPR) {
          icons.push("eye"); // View
          icons.push("download1"); // Download
          icons.push("edit"); // Edit
        } else {
          if (isCurrent || isFuture) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }
      // ENGINEER LOGIC
      else {
        if (hasMPR) {
          icons.push("eye"); // View

          if (isCurrent) {
            icons.push("edit"); // Edit
          } else if (isPast) {
            icons.push("download1"); // Download
          }
        } else {
          if (isCurrent) {
            icons.push("edit"); // Create
          } else {
            icons.push("close4"); // No entry
          }
        }
      }

      return {
        ...card,
        status,
        icons,
        hasMPR,
        mpr,
        isPast,
        isCurrent,
        isFuture,
      };
    });
  };

  // Get MOM cards
  const getMOMCards = () => {
    const isAdmin = isUserAdmin(userData?.role);

    // Display top 3 recent MOMs or empty cards if none
    const recentMOMs = momData.slice(0, 3);
    const cards = [];

    for (let i = 0; i < 3; i++) {
      const mom = recentMOMs[i];
      if (mom) {
        const icons = [];
        if (isAdmin) {
          icons.push("eye", "edit");
        } else {
          icons.push("eye");
          // Optionally add logic if engineer can edit DRAFT mom
        }

        cards.push({
          label: mom.meetingDate
            ? new Date(mom.meetingDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })
            : "MOM",
          range: mom.serialNumber
            ? `MOM Report #${mom.serialNumber}`
            : mom.agenda
              ? mom.agenda.length > 20
                ? mom.agenda.substring(0, 20) + "..."
                : mom.agenda
              : "-",
          status: "success",
          icons: icons,
          hasMOM: true,
          mom: mom,
        });
      } else {
        // Empty slot
        cards.push({
          label: "No MOM",
          range: "-",
          status: "error",
          icons: isAdmin ? ["edit"] : [], // Admin can click to create new from here? Actually the + button is better.
          hasMOM: false,
          mom: null,
        });
      }
    }
    return cards;
  };

  // Handle DPR icon click
  const handleDPRIconClick = (card, iconName) => {
    const dateString = card.actualDate.toISOString().split("T")[0];

    if (iconName === "edit") {
      navigate(`/progress/edit-dpr/${projectId}`, {
        state: {
          projectId: projectId,
          date: dateString,
          projectName: project?.projectName,
        },
      });
    } else if (iconName === "eye") {
      navigate(`/progress/view-dpr/${projectId}`, {
        state: {
          projectId: projectId,
          date: dateString,
          projectName: project?.projectName,
        },
      });
    } else if (iconName === "download1") {
      handleDownloadDPRReport(card.actualDate);
    } else if (iconName === "close4") {
      console.log("Close action for date:", card.date);
    }
  };

  // Handle WPR icon click
  const handleWPRIconClick = (card, iconName) => {
    const weekType = card.weekType;
    const weekStartDate = card.weekDates[0].date;
    const weekEndDate = card.weekDates[5].date;

    if (iconName === "edit") {
      const hasWPR = checkWPRExistsForWeek(card.weekDates);

      if (hasWPR) {
        navigate(`/progress/edit-wpr/${projectId}`, {
          state: {
            projectId: projectId,
            weekType: weekType,
            weekStartDate: weekStartDate,
            weekEndDate: weekEndDate,
            projectName: project?.projectName,
            cardData: card,
          },
        });
      } else {
        navigate(`/progress/add-wpr/${projectId}`, {
          state: {
            projectId: projectId,
            weekType: weekType,
            weekStartDate: weekStartDate,
            weekEndDate: weekEndDate,
            projectName: project?.projectName,
            cardData: card,
          },
        });
      }
    } else if (iconName === "eye") {
      navigate(`/progress/view-wpr/${projectId}`, {
        state: {
          projectId: projectId,
          weekType: weekType,
          weekStartDate: weekStartDate,
          weekEndDate: weekEndDate,
          projectName: project?.projectName,
          cardData: card,
        },
      });
    } else if (iconName === "download1") {
      handleDownloadWPRReport(card);
    } else if (iconName === "close4") {
      console.log("Close action for:", card.label);
    }
  };

  // Handle WMR icon click
  const handleWMRIconClick = (card, iconName) => {
    const weekType = card.weekType;
    const weekStartDate = card.weekDates[0].date;
    const weekEndDate = card.weekDates[5].date;

    if (iconName === "edit") {
      const wmr = card.wmr;

      if (wmr) {
        navigate(`/progress/edit-wmr/${projectId}/${wmr._id}`, {
          state: {
            projectId: projectId,
            weekType: weekType,
            weekStartDate: weekStartDate,
            weekEndDate: weekEndDate,
            projectName: project?.projectName,
            cardData: card,
          },
        });
      } else {
        navigate(`/progress/add-wmr/${projectId}`, {
          state: {
            projectId: projectId,
            weekType: weekType,
            weekStartDate: weekStartDate,
            weekEndDate: weekEndDate,
            projectName: project?.projectName,
            cardData: card,
          },
        });
      }
    } else if (iconName === "eye") {
      navigate(`/progress/view-wmr/${projectId}`, {
        state: {
          projectId: projectId,
          weekType: weekType,
          weekStartDate: weekStartDate,
          weekEndDate: weekEndDate,
          projectName: project?.projectName,
          cardData: card,
        },
      });
    } else if (iconName === "download1") {
      handleDownloadWMRReport(card);
    } else if (iconName === "close4") {
      console.log("Close action for:", card.label);
    }
  };

  // Handle MPR icon click
  const handleMPRIconClick = (card, iconName) => {
    const monthType = card.monthType;
    const monthStartDate = card.monthDates[0].date;
    const monthEndDate = card.monthDates[card.monthDates.length - 1].date;

    if (iconName === "edit") {
      const mpr = card.mpr;

      if (mpr) {
        navigate(`/progress/edit-mpr/${projectId}`, {
          state: {
            projectId: projectId,
            monthType: monthType,
            monthStartDate: monthStartDate,
            monthEndDate: monthEndDate,
            projectName: project?.projectName,
            project: project,
            cardData: card,
            mpr: mpr,
            selectedMonth: monthStartDate,
          },
        });
      } else {
        navigate(`/progress/add-mpr/${projectId}`, {
          state: {
            projectId: projectId,
            monthType: monthType,
            monthStartDate: monthStartDate,
            monthEndDate: monthEndDate,
            projectName: project?.projectName,
            project: project,
            cardData: card,
            selectedMonth: monthStartDate,
          },
        });
      }
    } else if (iconName === "eye") {
      navigate(`/progress/view-mpr/${projectId}`, {
        state: {
          projectId: projectId,
          monthType: monthType,
          monthStartDate: monthStartDate,
          monthEndDate: monthEndDate,
          projectName: project?.projectName,
          project: project,
          cardData: card,
          selectedMonth: monthStartDate,
        },
      });
    } else if (iconName === "download1") {
      handleDownloadMPRReport(card);
    } else if (iconName === "close4") {
      console.log("Close action for:", card.label);
    }
  };

  // Handle MOM icon click
  const handleMOMIconClick = (card, iconName) => {
    if (iconName === "edit" && card.hasMOM) {
      navigate(`/progress/edit-mom/${projectId}/${card.mom._id}`);
    } else if (iconName === "edit" && !card.hasMOM) {
      navigate(`/progress/add-mom/${projectId}`);
    } else if (iconName === "eye" && card.hasMOM) {
      navigate(`/progress/view-mom/${projectId}/${card.mom._id}`);
    }
  };

  // Handle engineer update
  const handleEngineerUpdate = (updatedProject) => {
    setProject(updatedProject);
    showToast("success", "Success", "Engineer updated successfully!");
  };

  const Section = ({
    title,
    cards,
    isDPR = false,
    isWPR = false,
    isWMR = false,
    isMPR = false,
    isMOM = false,
  }) => {
    const renderCards = isDPR
      ? getDPRCards()
      : isWPR
        ? getWPRCards()
        : isWMR
          ? getWMRCards()
          : isMPR
            ? getMPRCards()
            : isMOM
              ? getMOMCards()
              : cards;

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ minWidth: 50 }}>
            <Typography
              sx={{ fontWeight: 700, color: PRIMARY, whiteSpace: "nowrap" }}
            >
              {title}
            </Typography>

            {/* Show plus icon for admin/super admin only */}
            {isDPR && (
              <IconButton
                size="small"
                sx={{ bgcolor: PRIMARY, color: "#fff", mt: 1, borderRadius: 3 }}
                onClick={() => {
                  navigate(`/progress/add-dpr/${projectId}`, {
                    state: {
                      projectName: project?.projectName,
                      date: new Date().toISOString().split("T")[0],
                    },
                  });
                }}
              >
                <Icon name="plus2" />
              </IconButton>
            )}

            {isWPR && (
              <IconButton
                size="small"
                sx={{ bgcolor: PRIMARY, color: "#fff", mt: 1, borderRadius: 3 }}
                onClick={() => {
                  const currentWeekStart = currentWPRWeekDates[0].date;
                  const currentWeekEnd = currentWPRWeekDates[5].date;

                  navigate(`/progress/add-wpr/${projectId}`, {
                    state: {
                      projectName: project?.projectName,
                      weekType: "current",
                      weekStartDate: currentWeekStart,
                      weekEndDate: currentWeekEnd,
                    },
                  });
                }}
              >
                <Icon name="plus2" />
              </IconButton>
            )}

            {isWMR && (
              <IconButton
                size="small"
                sx={{ bgcolor: PRIMARY, color: "#fff", mt: 1, borderRadius: 3 }}
                onClick={() => {
                  const currentWeekStart = currentWPRWeekDates[0].date;
                  const currentWeekEnd = currentWPRWeekDates[5].date;

                  navigate(`/progress/add-wmr/${projectId}`, {
                    state: {
                      projectName: project?.projectName,
                      weekType: "current",
                      weekStartDate: currentWeekStart,
                      weekEndDate: currentWeekEnd,
                    },
                  });
                }}
              >
                <Icon name="plus2" />
              </IconButton>
            )}

            {isMPR && (
              <IconButton
                size="small"
                sx={{ bgcolor: PRIMARY, color: "#fff", mt: 1, borderRadius: 3 }}
                onClick={() => {
                  const currentMonthStart = currentMonthDates[0].date;
                  const currentMonthEnd =
                    currentMonthDates[currentMonthDates.length - 1].date;

                  navigate(`/progress/add-mpr/${projectId}`, {
                    state: {
                      projectName: project?.projectName,
                      project: project,
                      monthType: "current",
                      monthStartDate: currentMonthStart,
                      monthEndDate: currentMonthEnd,
                    },
                  });
                }}
              >
                <Icon name="plus2" />
              </IconButton>
            )}

            {isMOM && (
              <IconButton
                size="small"
                sx={{ bgcolor: PRIMARY, color: "#fff", mt: 1, borderRadius: 3 }}
                onClick={() => {
                  navigate(`/progress/add-mom/${projectId}`);
                }}
              >
                <Icon name="plus2" />
              </IconButton>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: isDPR
                ? isSmallScreen
                  ? 0.75
                  : 1.25
                : isSmallScreen
                  ? 1.25
                  : 2,
              flexWrap: "nowrap",
              width: "100%",
              overflow: "hidden",
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1, sm: 1.25 },
            }}
          >
            {renderCards &&
              renderCards.map((c, i) => (
                <Paper
                  key={i}
                  sx={{
                    flex: isDPR ? "1 1 calc(100% / 6)" : "1 1 calc(100% / 3)",
                    maxWidth: isDPR ? "calc(100% / 6)" : "calc(100% / 3)",
                    height: 95,
                    bgcolor:
                      isDPR || isWPR || isWMR || isMPR || isMOM
                        ? statusColor(c.status)
                        : "#FFFFFF",
                    borderRadius: 2,
                    p: 1.2,
                    position: "relative",
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                    border:
                      isDPR && c.actualDate && isToday(c.actualDate)
                        ? `2px solid ${PRIMARY}`
                        : "1px solid #e0e0e0",
                    minWidth: 0,
                  }}
                >
                  {/* For DPR cards */}
                  {isDPR ? (
                    <>
                      {/* Top-left: Day */}
                      <Typography
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          fontWeight: 600,
                          fontSize: isExtraSmall ? 11 : 12,
                          color: c.status === "info" ? "#000" : "#fff",
                        }}
                      >
                        {c.day}
                      </Typography>

                      {/* Center: Icons - REDUCED SPACING */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          gap: 0.25, // Reduced spacing
                        }}
                      >
                        {c.icons &&
                          c.icons.map((icon) => (
                            <CustomTooltip
                              title={
                                icon === "eye"
                                  ? "View DPR"
                                  : icon === "edit"
                                    ? c.hasDPR
                                      ? "Edit DPR"
                                      : "Create DPR"
                                    : icon === "download1"
                                      ? "Download DPR"
                                      : "No Entry"
                              }
                              key={icon}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleDPRIconClick(c, icon)}
                                disabled={
                                  icon === "download1" &&
                                  downloading?.type === "dpr" &&
                                  downloading?.date === c.actualDate
                                }
                                sx={{
                                  minWidth: 22, // Reduced size
                                  height: 22, // Reduced size
                                  padding: 0.25,
                                  "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                                }}
                              >
                                {icon === "download1" &&
                                downloading?.type === "dpr" &&
                                downloading?.date === c.actualDate ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <Icon name={icon} fontSize="small" />
                                )}
                              </IconButton>
                            </CustomTooltip>
                          ))}
                      </Box>

                      {/* Bottom-right: Date */}
                      <Typography
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          fontSize: isExtraSmall ? 9 : 10,
                          color: c.status === "info" ? "#000" : "#fff",
                        }}
                      >
                        {c.date}
                      </Typography>
                    </>
                  ) : (
                    // For WPR/WMR/MPR cards
                    <>
                      {/* Top-left: Label */}
                      <Typography
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          fontWeight: 600,
                          fontSize: isExtraSmall ? 11 : 12,
                          color: c.status === "info" ? "#000" : "#fff",
                        }}
                      >
                        {c.label}
                      </Typography>

                      {/* Center: Icons - REDUCED SPACING */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          gap: 0.25, // Reduced spacing
                        }}
                      >
                        {c.icons &&
                          c.icons.map((icon) => (
                            <CustomTooltip
                              title={
                                icon === "eye"
                                  ? isWPR
                                    ? "View WPR"
                                    : isWMR
                                      ? "View WMR"
                                      : "View MPR"
                                  : icon === "edit"
                                    ? (
                                        isWPR
                                          ? c.hasWPR
                                          : isWMR
                                            ? c.hasWMR
                                            : c.hasMPR
                                      )
                                      ? isWPR
                                        ? "Edit WPR"
                                        : isWMR
                                          ? "Edit WMR"
                                          : "Edit MPR"
                                      : isWPR
                                        ? "Create WPR"
                                        : isWMR
                                          ? "Create WMR"
                                          : "Create MPR"
                                    : icon === "download1"
                                      ? isWPR
                                        ? "Download WPR"
                                        : isWMR
                                          ? "Download WMR"
                                          : "Download MPR"
                                      : "No Entry"
                              }
                              key={icon}
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (isWPR) handleWPRIconClick(c, icon);
                                  if (isWMR) handleWMRIconClick(c, icon);
                                  if (isMPR) handleMPRIconClick(c, icon);
                                  if (isMOM) handleMOMIconClick(c, icon);
                                }}
                                disabled={
                                  icon === "download1" &&
                                  downloading?.type ===
                                    (isWPR ? "wpr" : isWMR ? "wmr" : "mpr") &&
                                  downloading?.id === c.label
                                }
                                sx={{
                                  minWidth: 22, // Reduced size
                                  height: 22, // Reduced size
                                  padding: 0.25,
                                  "&:hover": { bgcolor: "rgba(0,0,0,0.1)" },
                                }}
                              >
                                {icon === "download1" &&
                                downloading?.type ===
                                  (isWPR ? "wpr" : isWMR ? "wmr" : "mpr") &&
                                downloading?.id === c.label ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <Icon name={icon} fontSize="small" />
                                )}
                              </IconButton>
                            </CustomTooltip>
                          ))}
                      </Box>

                      {/* Bottom-right: Date Range */}
                      <Typography
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          fontSize: isExtraSmall ? 9 : 10,
                          color: c.status === "info" ? "#000" : "#fff",
                        }}
                      >
                        {c.range}
                      </Typography>
                    </>
                  )}
                </Paper>
              ))}
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ p: { xs: 0.5, md: 1 } }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="xl" sx={{ p: { xs: 0.5, md: 1 } }}>
        <Alert severity="warning">Project not found</Alert>
      </Container>
    );
  }

  const isAdmin = isUserAdmin(userData?.role);
  const statusColorStyle = getStatusColor(project.status);

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        p: { xs: 0.5, sm: 1, md: 1.5, lg: 2 },
        overflowX: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isSmallScreen ? "flex-start" : "flex-start",
          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          pb: 2,
          mb: 2,
          gap: isSmallScreen ? 2 : 0,
        }}
      >
        {/* Left Section */}
        <Box sx={{ width: isSmallScreen ? "100%" : "auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                ...TYPOGRAPHY.H2,
                color: PRIMARY,
                fontSize: isSmallScreen ? "1.25rem" : TYPOGRAPHY.H2.fontSize,
                wordBreak: "break-word",
              }}
            >
              {project.projectName}
            </Typography>
            <Chip
              label={getStatusText(project.status)}
              size="small"
              sx={{
                bgcolor: statusColorStyle.bgcolor,
                color: statusColorStyle.color,
                ...TYPOGRAPHY.BODY_SMALL,
                height: 24,
                fontWeight: 500,
                borderRadius: 4,
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          </Box>
          <Typography
            sx={{
              ...TYPOGRAPHY.BODY_LARGE,
              color: COLORS.TEXT,
              fontSize: isSmallScreen
                ? "0.85rem"
                : TYPOGRAPHY.BODY_LARGE.fontSize,
              mt: 0.5,
            }}
          >
            Developer: {project.developerName || "-"}
          </Typography>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            textAlign: isSmallScreen ? "left" : "right",
            width: isSmallScreen ? "100%" : "auto",
            mt: isSmallScreen ? 1 : 0,
          }}
        >
          {/* Icons Row - Hide edit engineer icon for non-admin users */}
          <Box
            sx={{
              display: "flex",
              justifyContent: isSmallScreen ? "flex-start" : "flex-end",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            {/* Only show Edit Assigned Engineer icon for admin users */}
            {isAdmin && (
              <CustomTooltip title="Edit Assigned Engineer">
                <IconButton
                  size="small"
                  onClick={() => setEditEngineerModalOpen(true)}
                  sx={{ minWidth: 32, height: 32 }}
                >
                  <Icon name="Action5" color={"#66bb6a"} fontSize="small" />
                </IconButton>
              </CustomTooltip>
            )}

            {/* Refresh DPR Data button */}
            {isAdmin && (
              <CustomTooltip title="Refresh DPR Data">
                <IconButton
                  size="small"
                  onClick={refreshDPRData}
                  disabled={refreshing}
                  sx={{ minWidth: 32, height: 32 }}
                >
                  {refreshing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon
                      name="refresh"
                      color={COLORS.PRIMARY}
                      fontSize="small"
                    />
                  )}
                </IconButton>
              </CustomTooltip>
            )}

            {isAdmin && (
              <CustomTooltip title="Edit Project">
                <IconButton
                  size="small"
                  onClick={() =>
                    navigate(`/progress/edit-inhouse-project/${projectId}`)
                  }
                  sx={{ minWidth: 32, height: 32 }}
                >
                  <Icon
                    name="Action3"
                    color={COLORS.PRIMARY}
                    fontSize="small"
                  />
                </IconButton>
              </CustomTooltip>
            )}
            {isAdmin && (
              <CustomTooltip title="Delete Project">
                <IconButton size="small" sx={{ minWidth: 32, height: 32 }}>
                  <Icon name="Action4" color={COLORS.ERROR} fontSize="small" />
                </IconButton>
              </CustomTooltip>
            )}
            <CustomTooltip title="Close">
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                sx={{ minWidth: 32, height: 32 }}
              >
                <Icon name="close3" color={COLORS.ERROR} fontSize="small" />
              </IconButton>
            </CustomTooltip>
          </Box>

          {/* Dates */}
          <Box
            sx={{
              display: "flex",
              gap: isSmallScreen ? 1 : 3,
              justifyContent: isSmallScreen ? "space-between" : "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                textAlign: isSmallScreen ? "left" : "right",
                minWidth: isSmallScreen ? "45%" : "auto",
              }}
            >
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: "#000",
                  fontSize: isSmallScreen
                    ? "0.7rem"
                    : TYPOGRAPHY.BODY_SMALL.fontSize,
                }}
              >
                Start Date
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: isSmallScreen ? "flex-start" : "flex-end",
                }}
              >
                <Icon name="calendar3" color={PRIMARY} fontSize="small" />
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontSize: isSmallScreen ? "0.8rem" : TYPOGRAPHY.H4.fontSize,
                  }}
                >
                  {formatDate(project.projectStartDate)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                textAlign: isSmallScreen ? "left" : "right",
                minWidth: isSmallScreen ? "45%" : "auto",
              }}
            >
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: "#000",
                  fontSize: isSmallScreen
                    ? "0.7rem"
                    : TYPOGRAPHY.BODY_SMALL.fontSize,
                }}
              >
                End Date
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: isSmallScreen ? "flex-start" : "flex-end",
                }}
              >
                <Icon name="endDate" color={PRIMARY} fontSize="small" />
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    fontSize: isSmallScreen ? "0.8rem" : TYPOGRAPHY.H4.fontSize,
                  }}
                >
                  {formatDate(project.projectEndDate)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "3fr 1px 1fr",
          gap: isSmallScreen ? 2 : 3,
          overflow: "hidden",
        }}
      >
        {/* LEFT COLUMN */}
        <Box>
          {/* Assigned / Stakeholders */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 3,
              py: 2,
            }}
          >
            {/* Assigned Engineer */}
            <Box>
              <Typography sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}>
                Assigned Engineer:{" "}
                <Box
                  component="span"
                  sx={{ color: COLORS.PRIMARY, fontWeight: 500 }}
                >
                  {project.assignedEngineer?.name ||
                    project.assignedEngineerName ||
                    "Not Assigned"}
                </Box>
              </Typography>

              {project.assignedEngineer?.email && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                >
                  <Icon name="at" size={16} color={COLORS.PRIMARY} />
                  <Typography sx={{ fontSize: 13, color: COLORS.PRIMARY }}>
                    {project.assignedEngineer.email}
                  </Typography>
                </Box>
              )}

              {project.assignedEngineer?.phone && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Icon name="phoneDone" size={16} color={COLORS.PRIMARY} />
                  <Typography sx={{ fontSize: 13, color: COLORS.PRIMARY }}>
                    {project.assignedEngineer.phone}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Stakeholders */}
            {project.stakeholders && project.stakeholders.length > 0 && (
              <Box>
                <Typography
                  sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE }}
                >
                  Stakeholders:
                </Typography>

                {project.stakeholders.map((stakeholder, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: 13, color: COLORS.PRIMARY, mt: 1 }}
                  >
                    {stakeholder.role}: {stakeholder.name}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

          {/* DPR Section with dynamic cards */}
          <Section title="DPR" cards={[]} isDPR={true} />

          <Divider sx={{ my: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

          {/* WPR Section */}
          <Section title="WPR" cards={weekCards} isWPR={true} />

          <Divider sx={{ my: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

          {/* WMR Section */}
          <Section title="WMR" cards={weekCards} isWMR={true} />

          <Divider sx={{ my: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

          {/* MPR Section */}
          <Section title="MPR" cards={monthCards} isMPR={true} />

          {/* <Divider sx={{ my: 3, borderColor: COLORS.LIGHT_SECONDARY }} /> */}

          {/* MOM Section */}
          {/* <Section title="MOM" cards={[]} isMOM={true} /> */}
        </Box>

        {/* Divider */}
        {!isMobile && (
          <Box
            sx={{
              bgcolor: COLORS.LIGHT_SECONDARY,
              width: 1,
              mx: isSmallScreen ? 0 : 1,
            }}
          />
        )}

        {/* RIGHT COLUMN */}
        <Box
          sx={{
            minWidth: isSmallScreen ? "100%" : 260,
            overflow: "hidden",
            mt: isMobile ? 2 : 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              mb: 2,
              color: COLORS.SECONDARY,
              ...TYPOGRAPHY.H4,
              fontSize: isSmallScreen ? "0.9rem" : TYPOGRAPHY.H4.fontSize,
            }}
          >
            Click On Card to See Reports:
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1,
            }}
          >
            {["DPR", "WPR", "MPR"].map((r, i) => (
              <Paper
                key={r}
                elevation={2}
                sx={{
                  py: isSmallScreen ? 1.5 : 2,
                  textAlign: "center",
                  borderRadius: 2,
                  minHeight: isSmallScreen ? 65 : 70,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.02)",
                  },
                }}
                onClick={() => {
                  if (r === "DPR") {
                    navigate(`/progress/dpr`, {
                      state: {
                        projectId: projectId,
                        projectName: project.projectName,
                      },
                    });
                  } else if (r === "WPR") {
                    navigate(`/progress/wpr`, {
                      state: {
                        projectId: projectId,
                        projectName: project.projectName,
                      },
                    });
                  } else if (r === "MPR") {
                    navigate(`/progress/mpr`, {
                      state: {
                        projectId: projectId,
                        projectName: project.projectName,
                      },
                    });
                  } else if (r === "MOM") {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? 18 : 22,
                    fontWeight: 700,
                    color: COLORS.SECONDARY,
                    lineHeight: 1,
                  }}
                >
                  {[project.DPR || 0, project.WPR || 0, project.MPR || 0][i]
                    .toString()
                    .padStart(2, "0")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: isSmallScreen ? 10 : 11,
                    color: COLORS.TEXT,
                    mt: 0.5,
                  }}
                >
                  {r}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Edit Engineer Modal - Only for admin users */}
      {project && isAdmin && (
        <EditEngineerModal
          open={editEngineerModalOpen}
          onClose={() => setEditEngineerModalOpen(false)}
          project={project}
          onUpdate={handleEngineerUpdate}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Toast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />
    </Container>
  );
};

export default ProjectOverview;
